import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  User,
  Mail,
  Briefcase,
  ShieldCheck,
  Pencil,
  Trash2,
  Power,
  Wallet,
  DollarSign,
  FileText,
  BookOpen,
  Lock,
  Info,
} from "lucide-react";
import EmployeeDocumentAccessPanel from "./EmployeeDocumentAccessPanel";
import EmployeeHrDocumentsPanel from "./EmployeeHrDocumentsPanel";
import { labelForBankCode } from "../../../data/vietnamBanks";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";
import { hasEmployeeSensitivePermission } from "../../../utils/permissionUtils";

const formatDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("vi-VN");
};

const display = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const money = (v) =>
  v != null ? Number(v).toLocaleString("vi-VN") : "—";

const Card = ({ icon: Icon, title, children, hint }) => (
  <section className="space-y-3 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50/80 to-white p-4 sm:space-y-4 sm:p-5">
    <div className="flex items-center gap-2.5">
      <div className="rounded-xl border border-red-700/10 bg-red-700/5 p-2 text-red-700">
        <Icon className="h-4 w-4" strokeWidth={2.25} />
      </div>
      <h3 className="text-sm font-black uppercase tracking-wide text-gray-800">
        {title}
      </h3>
    </div>
    {hint ? (
      <p className="text-[11px] leading-relaxed text-gray-500 sm:text-xs -mt-1">
        {hint}
      </p>
    ) : null}
    {children}
  </section>
);

/** Hàng 2 cột: label | value — dễ quét trên mobile */
const Field = ({ label, children, className = "" }) => (
  <div
    className={`grid grid-cols-[7.5rem_minmax(0,1fr)] items-start gap-x-3 gap-y-0.5 border-b border-gray-100/80 py-2.5 last:border-b-0 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-x-4 ${className}`}
  >
    <span className="pt-0.5 text-[11px] font-bold text-gray-500 sm:text-xs">
      {label}
    </span>
    <div className="min-w-0 text-sm font-semibold leading-snug text-gray-800 break-words">
      {children}
    </div>
  </div>
);

const FieldList = ({ children }) => (
  <div className="divide-y divide-transparent -my-1">{children}</div>
);

const LockedPanel = ({ title, hint }) => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-amber-200 bg-amber-50/80 px-4 py-10 text-center sm:px-6 sm:py-14">
    <div className="rounded-2xl bg-amber-100 p-3 text-amber-800">
      <Lock className="h-6 w-6" strokeWidth={2.2} />
    </div>
    <p className="text-sm font-black uppercase tracking-wide text-amber-950">
      {title}
    </p>
    <p className="max-w-md text-xs leading-relaxed text-amber-900/80">{hint}</p>
  </div>
);

const StatusBadge = ({ isActive }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-widest ${
      isActive
        ? "border-emerald-100 bg-emerald-50 text-emerald-600"
        : "border-rose-100 bg-rose-50 text-rose-600"
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${
        isActive ? "animate-pulse bg-emerald-500" : "bg-rose-400"
      }`}
    />
    {isActive ? "Đang làm việc" : "Ngưng sử dụng"}
  </span>
);

const EmployeeDetailModal = ({
  isOpen,
  employee,
  onClose,
  onEdit,
  onDelete,
  onToggleStatus,
}) => {
  const [activeTab, setActiveTab] = useState("profile");

  useEffect(() => {
    if (isOpen) setActiveTab("profile");
  }, [isOpen, employee?.id]);

  const canShowSensitive = !!employee?._sensitiveVisible;
  const canEditSensitive = hasEmployeeSensitivePermission("U");
  const contracts = Array.isArray(employee?.contracts) ? employee.contracts : [];

  const formTabs = useMemo(() => {
    const tabs = [
      { id: "profile", label: "Hồ sơ", shortLabel: "Hồ sơ", icon: User },
    ];
    if (canShowSensitive) {
      tabs.push(
        {
          id: "sensitive",
          label: "Dữ liệu nhạy cảm",
          shortLabel: "Nhạy cảm",
          icon: ShieldCheck,
        },
        {
          id: "payroll",
          label: "Lương & BHXH",
          shortLabel: "Lương",
          icon: Wallet,
        },
        {
          id: "contract",
          label: "Hợp đồng",
          shortLabel: "HĐ",
          icon: Briefcase,
        }
      );
    }
    tabs.push({
      id: "elearning",
      label: "Elearning",
      shortLabel: "EL",
      icon: BookOpen,
    });
    return tabs;
  }, [canShowSensitive]);

  useEffect(() => {
    if (!formTabs.some((t) => t.id === activeTab)) {
      setActiveTab("profile");
    }
  }, [formTabs, activeTab]);

  if (typeof document === "undefined") return null;
  if (!isOpen || !employee) return null;

  const displayName = employee.name || employee.employee_name || "Nhân sự";
  const displayCode = employee.code || employee.employee_code || "—";

  const handleDeleteClick = async () => {
    const confirm = await confirmDelete({
      type: DELETE_ENTITY.employee,
      name: displayName,
      context: { code: displayCode },
    });
    if (!confirm.isConfirmed) return;
    onDelete?.(employee.id);
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/45 backdrop-blur-sm"
            onClick={onClose}
          />

          <div className="fixed inset-0 z-[210] flex items-end justify-center p-0 pointer-events-none sm:items-center sm:p-4">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 320 }}
              className="pointer-events-auto flex h-[92dvh] w-full max-w-5xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-[min(860px,90vh)] sm:rounded-[2rem]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 bg-gradient-to-r from-red-50/70 via-white to-white px-4 py-4 sm:px-6">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <img
                    src={
                      employee.avatar_url ||
                      "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                    }
                    alt={displayName}
                    className="h-12 w-12 shrink-0 rounded-2xl object-cover ring-2 ring-red-100 sm:h-14 sm:w-14"
                  />
                  <div className="min-w-0">
                    <h2 className="truncate text-lg font-black uppercase tracking-tight text-gray-800 sm:text-xl">
                      {displayName}
                    </h2>
                    <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                      Mã NV · {displayCode}
                    </p>
                    <div className="mt-2">
                      <StatusBadge isActive={!!employee.is_active} />
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-gray-200 bg-white p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                  aria-label="Đóng"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Tabs */}
              <div className="shrink-0 border-b border-gray-100 bg-white px-2 sm:px-4">
                <div className="flex gap-0.5 overflow-x-auto overscroll-x-contain -mb-px no-scrollbar">
                  {formTabs.map((tab) => {
                    const Icon = tab.icon;
                    const active = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex shrink-0 items-center gap-1.5 border-b-2 px-2.5 py-3 text-[11px] font-bold uppercase tracking-wide transition-colors sm:gap-2 sm:px-3 sm:text-xs ${
                          active
                            ? "border-red-600 text-red-700"
                            : "border-transparent text-gray-400 hover:text-gray-700"
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">{tab.label}</span>
                        <span className="sm:hidden">{tab.shortLabel}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Body */}
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain bg-slate-50/40 px-4 py-4 sm:space-y-5 sm:px-6 sm:py-5">
                {activeTab === "profile" && (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
                    <Card icon={User} title="Thông tin cơ bản">
                      <FieldList>
                        <Field label="Họ tên">{displayName}</Field>
                        <Field label="Chi nhánh">
                          {display(employee.branch_name)}
                        </Field>
                        <Field label="Bộ phận">
                          {display(employee.department_name)}
                        </Field>
                        <Field label="Chức vụ">
                          {display(
                            employee.position_display ||
                              employee.position_name ||
                              employee.position
                          )}
                        </Field>
                        <Field label="Phân loại">
                          {display(employee.employment_classification)}
                        </Field>
                        <Field label="Giáo viên / GV">
                          {employee.is_teacher ? "Có" : "Không"}
                        </Field>
                        <Field label="Trạng thái">
                          <StatusBadge isActive={!!employee.is_active} />
                        </Field>
                      </FieldList>
                    </Card>

                    <Card icon={Mail} title="Liên hệ">
                      <FieldList>
                        <Field label="Email công việc">
                          <span className="break-all">
                            {display(employee.email)}
                          </span>
                        </Field>
                        <Field label="Số điện thoại">
                          {display(employee.phone)}
                        </Field>
                        <Field label="Ngày sinh">
                          {formatDate(employee.birthdate || employee.birthday)}
                        </Field>
                        <Field label="Giới tính">
                          {employee.gender === "male"
                            ? "Nam"
                            : employee.gender === "female"
                              ? "Nữ"
                              : display(employee.gender)}
                        </Field>
                        <Field label="Ngày tạo hồ sơ">
                          {formatDate(employee.created_at)}
                        </Field>
                      </FieldList>
                    </Card>
                  </div>
                )}

                {activeTab === "sensitive" && (
                  <>
                    {!canShowSensitive ? (
                      <LockedPanel
                        title="Không có quyền xem dữ liệu nhạy cảm"
                        hint="Cần quyền hrm.employees_sensitive (R) để xem CCCD, địa chỉ, ngày phép…"
                      />
                    ) : (
                      <Card icon={ShieldCheck} title="Thông tin nhạy cảm">
                        <FieldList>
                          <Field label="CMND / CCCD">
                            {display(employee.personal_id)}
                          </Field>
                          <Field label="Email cá nhân">
                            <span className="break-all">
                              {display(employee.personal_email)}
                            </span>
                          </Field>
                          <Field label="Ngày phép / năm">
                            {display(employee.annual_leave_total)}
                          </Field>
                          <Field label="Địa chỉ">
                            {display(employee.address)}
                          </Field>
                          <Field label="Ghi chú">
                            {display(employee.remark)}
                          </Field>
                        </FieldList>
                      </Card>
                    )}
                  </>
                )}

                {activeTab === "payroll" && (
                  <>
                    {!canShowSensitive ? (
                      <LockedPanel
                        title="Không có quyền xem lương & BHXH"
                        hint="Cần quyền hrm.employees_sensitive để xem tài khoản nhận lương và thông tin BHXH / thuế."
                      />
                    ) : (
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
                        <Card icon={Wallet} title="Tài khoản nhận lương">
                          <FieldList>
                            <Field label="Ngân hàng">
                              {labelForBankCode(employee.payroll_bank_code) ||
                                "—"}
                            </Field>
                            <Field label="Số tài khoản">
                              {display(employee.payroll_bank_account_number)}
                            </Field>
                            <Field label="Tên tài khoản">
                              {display(employee.payroll_bank_account_name)}
                            </Field>
                          </FieldList>
                        </Card>

                        <Card icon={DollarSign} title="BHXH & Thuế TNCN">
                          <FieldList>
                            <Field label="Tham gia BHXH">
                              {employee.insurance_enrolled === false
                                ? "Không"
                                : "Có"}
                            </Field>
                            <Field label="Mức đóng BHXH">
                              {employee.insurance_salary != null
                                ? money(employee.insurance_salary)
                                : "Theo lương HĐ"}
                            </Field>
                            <Field label="Người phụ thuộc">
                              {Number(employee.tax_dependent_count) || 0}
                            </Field>
                            <Field label="Cư trú TNCN">
                              {employee.pit_resident === false
                                ? "Không cư trú (20%)"
                                : "Cư trú (lũy tiến)"}
                            </Field>
                          </FieldList>
                        </Card>
                      </div>
                    )}
                  </>
                )}

                {activeTab === "contract" && (
                  <>
                    {!canShowSensitive ? (
                      <LockedPanel
                        title="Không có quyền xem hợp đồng"
                        hint="Cần quyền hrm.employees_sensitive để xem tài liệu và thông tin HĐ lao động."
                      />
                    ) : (
                      <div className="mx-auto max-w-3xl space-y-4 sm:space-y-5">
                        <div className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-xs leading-relaxed text-slate-700">
                          <Info className="mt-0.5 h-4 w-4 shrink-0 opacity-80" />
                          <p>
                            <b>Tài liệu nhân sự</b> = kho file lưu trữ.{" "}
                            <b>Thông tin HĐ</b> = dữ liệu số (lương/phụ cấp) dùng
                            khi tính lương — hai mục độc lập.
                          </p>
                        </div>

                        <Card
                          icon={FileText}
                          title="Tài liệu nhân sự"
                          hint="Kho file hồ sơ (HĐLĐ, hồ sơ xin việc, PV…). Chỉ lưu trữ / xem — không dùng để tính lương."
                        >
                          <EmployeeHrDocumentsPanel
                            employeeId={employee.id}
                            canEdit={canEditSensitive}
                            showPayrollHint={false}
                          />
                        </Card>

                        <Card
                          icon={Briefcase}
                          title="Thông tin HĐ lao động"
                          hint="Dữ liệu số trên HĐ (lương / phụ cấp) dùng khi tính lương."
                        >
                          {contracts.length === 0 ? (
                            <p className="text-sm italic text-gray-500">
                              Chưa có hợp đồng.
                            </p>
                          ) : (
                            <>
                              {/* Mobile cards */}
                              <ul className="space-y-2.5 md:hidden">
                                {contracts.map((c) => (
                                  <li
                                    key={c.id}
                                    className="rounded-xl border border-gray-200 bg-white p-3.5"
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="min-w-0">
                                        <p className="text-sm font-bold text-gray-900">
                                          {display(c.contract_no)}
                                        </p>
                                        <p className="mt-0.5 text-xs text-gray-500">
                                          {display(c.contract_type)}
                                        </p>
                                      </div>
                                      {c.is_active ? (
                                        <span className="shrink-0 rounded-full border border-emerald-100 bg-emerald-50 px-2 py-0.5 text-[9px] font-black uppercase text-emerald-700">
                                          HL
                                        </span>
                                      ) : null}
                                    </div>
                                    <div className="mt-2 border-t border-gray-50 pt-1">
                                      <FieldList>
                                        <Field label="Hiệu lực">
                                          {formatDate(c.start_date)}
                                        </Field>
                                        <Field label="Hết hạn">
                                          {formatDate(c.end_date)}
                                        </Field>
                                        <Field label="Lương">
                                          {money(c.salary)}
                                        </Field>
                                        <Field label="Phụ cấp">
                                          {money(c.allowance)}
                                        </Field>
                                      </FieldList>
                                    </div>
                                  </li>
                                ))}
                              </ul>

                              {/* Desktop table */}
                              <div className="hidden overflow-x-auto md:block">
                                <table className="w-full overflow-hidden rounded-xl border border-gray-200 text-sm">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="border-b p-2 text-left">
                                        Số HĐ
                                      </th>
                                      <th className="border-b p-2 text-left">
                                        Loại
                                      </th>
                                      <th className="border-b p-2 text-left">
                                        Hiệu lực
                                      </th>
                                      <th className="border-b p-2 text-left">
                                        Hết hạn
                                      </th>
                                      <th className="border-b p-2 text-right">
                                        Lương
                                      </th>
                                      <th className="border-b p-2 text-right">
                                        Phụ cấp
                                      </th>
                                      <th className="border-b p-2 text-center">
                                        TT
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {contracts.map((c) => (
                                      <tr
                                        key={c.id}
                                        className="border-b last:border-b-0"
                                      >
                                        <td className="p-2">
                                          {display(c.contract_no)}
                                        </td>
                                        <td className="p-2">
                                          {display(c.contract_type)}
                                        </td>
                                        <td className="p-2">
                                          {formatDate(c.start_date)}
                                        </td>
                                        <td className="p-2">
                                          {formatDate(c.end_date)}
                                        </td>
                                        <td className="p-2 text-right">
                                          {money(c.salary)}
                                        </td>
                                        <td className="p-2 text-right">
                                          {money(c.allowance)}
                                        </td>
                                        <td className="p-2 text-center">
                                          {c.is_active ? (
                                            <span className="rounded-md bg-green-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-green-700">
                                              HL
                                            </span>
                                          ) : (
                                            <span className="text-gray-300">
                                              —
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          )}
                        </Card>
                      </div>
                    )}
                  </>
                )}

                {activeTab === "elearning" && (
                  <div className="mx-auto max-w-3xl">
                    <EmployeeDocumentAccessPanel employeeId={employee.id} />
                  </div>
                )}
              </div>

              {/* Footer — 1 hàng trên mobile */}
              <div className="flex shrink-0 items-center gap-2 border-t border-gray-100 bg-gray-50/80 px-4 py-3 sm:justify-end sm:gap-3 sm:px-6 sm:py-4">
                <button
                  type="button"
                  onClick={() =>
                    onToggleStatus?.(employee.id, !employee.is_active)
                  }
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-gray-200 bg-white px-2 py-2.5 text-[10px] font-bold uppercase tracking-wider text-gray-700 transition hover:bg-gray-50 sm:flex-none sm:gap-2 sm:px-4 sm:py-2.5 sm:text-xs"
                >
                  <Power size={14} className="shrink-0" />
                  <span className="truncate">
                    {employee.is_active ? "Ngưng" : "Kích hoạt"}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => onEdit?.(employee.id)}
                  className="hidden md:inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-red-700 to-red-800 px-2 py-2.5 text-[10px] font-black uppercase tracking-wider text-white shadow-md shadow-red-100 transition hover:shadow-lg md:flex-none md:gap-2 md:px-4 md:py-2.5 md:text-xs"
                >
                  <Pencil size={14} className="shrink-0" />
                  <span className="truncate">Sửa</span>
                </button>
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-red-200 bg-white px-2 py-2.5 text-[10px] font-bold uppercase tracking-wider text-red-700 transition hover:bg-red-50 sm:flex-none sm:gap-2 sm:px-4 sm:py-2.5 sm:text-xs"
                >
                  <Trash2 size={14} className="shrink-0" />
                  <span className="truncate">Xóa</span>
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default EmployeeDetailModal;
